import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, type ReactNode } from "react";
import { ApiError, api, setUnauthorizedHandler } from "../api/client.js";
import type { Me } from "../api/types.js";

interface AuthState {
  user: Me | null;
  loading: boolean;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: async (): Promise<Me | null> => {
      try {
        return await api.get<Me>("/api/me");
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    setUnauthorizedHandler(() => queryClient.setQueryData(["me"], null));
  }, [queryClient]);

  const state: AuthState = {
    user: data ?? null,
    loading: isLoading,
    async login(username, password) {
      const me = await api.post<Me>("/api/auth/login", { username, password });
      queryClient.setQueryData(["me"], me);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
    },
    async logout() {
      try {
        await api.post("/api/auth/logout");
      } finally {
        // Even if the request fails, the user clicked "sign out" — drop the
        // local session view instead of leaving the UI silently logged in.
        queryClient.setQueryData(["me"], null);
        queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
      }
    },
  };

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const state = useContext(AuthContext);
  if (!state) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return state;
}
